import {
  ATypeCardView,
  BTypeBreadCrumb,
  CTypeFacilityMainHead,
  CTypeMap,
  CTypeTabsFacility,
  ClickEventType,
  DTypeCTA,
  DTypeFacilityFAQ,
  FacilityDetailFAQ,
  DTypeFacilityList,
  DefaultFacility,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildFacilityPageSchema,
  GOI_WEB_URL,
  RichSearch,
  ParlorHeadSection,
  useFacilities,
  excludeFacilities,
  FacilityHiddenNotice,
  getFacilityTypeLabel,
  getFacilityDisplayName,
  getFacilityRegionKeyword,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

import facilities from "goi_common/src/constants/facilities_detail.json";

import { parlorRegion } from "@/constants/parlorRegion";
import { Stack } from "@mui/material";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((facility) => targetSidos.includes(facility.sido))
    .map((facility) => ({
      params: {
        id: String(facility.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string;

  const p_facility = facilities.find(
    (facility) => Number(facility.id) === Number(id)
  ) as DefaultFacility;

  const { address, companyname, images, type } = p_facility;
  const typeLabel = getFacilityTypeLabel(type);
  const displayName = getFacilityDisplayName(companyname, type);
  const regionKeyword = getFacilityRegionKeyword(type);

  const 시가아닌_도인경우 = address.split(" ")[0].length == 4;
  const district = 시가아닌_도인경우
    ? address[0] + address[2]
    : address.slice(0, 2);

  const title = `(${district}) ${displayName} 정보, 비용, 가격, 예약 정리 | 충청대전세종장례소개포털`;
  const description = `${district} 지역 ${regionKeyword} 중 ${companyname}의 정보, 비용, 가격, 예약에 관하여 자세하게 알아보세요.`;
  const keywords = `${district} ${regionKeyword}, ${companyname}, ${companyname} 정보, ${companyname} 비용, ${companyname} 평균 예상 비용, ${companyname} 자연장, ${companyname} 수목장, ${companyname} 추천, ${companyname} 시설, ${companyname} 위치, ${companyname} 주소, ${displayName}, ${companyname} 후기`;

  return {
    props: {
      defaultFacility: p_facility,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgImage: images[0].url || "",
    },
  };
};

interface FacilityDetailPagProps {
  pageOgTitle: string;
  defaultFacility: DefaultFacility;
}

const FacilityDetailPage = ({
  pageOgTitle,
  defaultFacility,
}: FacilityDetailPagProps) => {
  const sidoName = FUNERAL_SIDO.get(Number(defaultFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultFacility.sido))?.get(
    Number(defaultFacility.sigungu)
  );

  const facilityPageSchema = buildFacilityPageSchema(defaultFacility);

  const { facilityList, facilityPrices } = useFacilities({
    sido: defaultFacility.sido,
  });

  const currentPageEvent: ClickEventType = {
    domain: "ch",
    path: "facil_item_intro",
  };

  const isHidden = excludeFacilities.includes(String(defaultFacility.id));

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqList) }}
        /> */}
      </Head>
      <RichSearch data={facilityPageSchema} />
      <BTypeBreadCrumb
        firstItem={{
          text: `장지 추천`,
          href: isHidden
            ? `${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`
            : `${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`,
          event: {
            ...currentPageEvent,
            trigger_and_target: "br_crumb_mth_facil_req",
          },
        }}
        // secondItem={{
        //   text: `${sidoName} 장례식장`,
        //   href: `/parlors-list/${sidoName} 의 장례식장 목록/`,
        //   event: {
        //     domain: "ch",
        //     path: "prlr_item_intro",
        //     trigger_and_target: "br_crumb_sido_prlr_list",
        //   },
        // }}
        // thirdItem={{
        //   text: `${sigunguName} 장례식장`,
        //   href: `/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
        //   event: {
        //     domain: "ch",
        //     path: "prlr_item_intro",
        //     trigger_and_target: "br_crumb_sigungu_prlr_list",
        //   },
        // }}
        goiHomeEvent={{
          ...currentPageEvent,
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <ParlorHeadSection
        component={
          <>
            <CTypeFacilityMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              mainColor={regionColor}
              defaultFacility={defaultFacility}
              title={`${defaultFacility.companyname}`}
              event={{
                ...currentPageEvent,
                trigger_and_target: "fac_goi_call",
              }}
            />
          </>
        }
      />
      {isHidden ? (
        <FacilityHiddenNotice
          companyname={defaultFacility.companyname}
          mainColor={regionColor}
          ctaHref={`${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`}
          event={{
            ...currentPageEvent,
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      ) : (
        <>
      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <DTypeCTA
          sidoName={sidoName}
          sigunguName={sigunguName}
          mainColor={regionColor}
          text="장지 비용 알아보기"
          companyname={defaultFacility.companyname}
          href={`${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`}
          event={{
            ...currentPageEvent,
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      </Stack>
      <Stack style={{ background: "white" }}>
        <CTypeTabsFacility
          href={`facility-item/${defaultFacility.id}`}
          mainColor={regionColor}
          selectTab="소개"
          event={{
            ...currentPageEvent,
            trigger_and_target: "CType_facil_tab",
          }}
        />
      </Stack>

      <Stack padding={"40px 20px"}>
        <DTypeFacilityFAQ />
        <FacilityDetailFAQ />
      </Stack>

      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <CTypeMap defaultParlor={defaultFacility} />
      </Stack>
        </>
      )}

      <Stack
        style={{
          padding: "40px 0px",
        }}
      >
        <DTypeFacilityList
          title={`${sidoName} 지역의 다른 장지`}
          mainColor={regionColor}
        >
          <Stack component="ul" gap="16px">
            {facilityList.length > 0 &&
              facilityList?.map((res, index) => {
                const { max, min } = facilityPrices[index];
                const averagePrice = (max + min) / 2;

                return (
                  <ATypeCardView
                    key={res.companyname}
                    name={res.companyname}
                    href={`/facility-item/${res.id}/`}
                    arrowText={`${getFacilityDisplayName(res.companyname, res.type)} 자세히 알아보기`}
                    descriptions={[
                      `장지 평균 비용 : ${averagePrice.toLocaleString()}원`,
                    ]}
                    imgSrc={res.images?.[0].url || ""}
                    event={{
                      ...currentPageEvent,
                      trigger_and_target: "uv_facility_price",
                    }}
                  />
                );
              })}
          </Stack>
        </DTypeFacilityList>
      </Stack>
    </>
  );
};

export default FacilityDetailPage;
